// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import {
    T_FOOTER_MAINTENANCE_24,
    T_FOOTER_TITLE_SERVICES,
    T_FOOTER_TITLE_CUSTOMER_SERVICES,
    T_FOOTER_TITLE_LINKS,
    T_SUPPORT_INFO,
    T_SUPPORT_EMAIL,
    T_SUPPORT_EMAIL_HREF,
    T_FOOTER_TITLE_TERMS_LEGALS,
    T_FOOTER_TERMS_OF_DELIVERY,
    T_FOOTER_PRIVACY_POLICY,
    T_FOOTER_COOKIES,
    T_NOTIFICATIONS,
    T_FOOTER_ADDRESS_TITLE,
    T_SITE_NAME,
    T_FOOTER_TITLE,
    T_FOOTER_DESCRIPTION,
} from "../../../../core/constants/translation";
import {
    NavLink
} from "react-router-dom";
import {
    SendanorLogoWhite
} from "../../../assets/logos";
import {
    MAINTENANCE_PHONE_NUMBER_DISPLAY,
    COMPANY_PHONE_NUMBER_DISPLAY,
    SENDANOR_SHELL_WEBSITE,
    SENDANOR_MAIL_WEBSITE,
    COMPANY_TWITTER_URL,
    COMPANY_FACEBOOK_URL,
    COMPANY_GITHUB_URL,
    COMPANY_LINKEDIN_URL,
    COMPANY_DISCORD_URL,
    SENDANOR_NOTIFICATIONS_WEBSITE,
    COMPANY_ADDRESS_LINE_1,
    COMPANY_ADDRESS_LINE_2,
    SENDANOR_VPS_WEBSITE,
    SENDANOR_PROJECT_WEBSITE,
    SENDANOR_PROJECT_TRACKER_WEBSITE,
    PAYTRAIL_ENABLED,
} from "../../../../core/constants/company";
import {
    COOKIES_ROUTE,
    FOOTER_ROUTE,
    INDEX_ROUTE,
    PRIVACY_POLICY_ROUTE,
    TERMS_OF_DELIVERY_ROUTE,
} from "../../../../core/constants/route";
import { APP_FOOTER, APP_CLASS_NAME } from "../../../../core/constants/className";
import { useMenuItems } from "../../../hooks/useMenuItems";
import { PhoneLink } from "../PhoneLink";
import { NavMenu } from "../navMenu/NavMenu";
import { SEODiv } from "../seo/SEODiv";
import { SocialMediaLinkList } from "../socialMediaLinkList/SocialMediaLinkList";
import { PaymentOptionList } from "../paymentOptionList/PaymentOptionList";
import { LegalInfo } from "../legal/LegalInfo";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import "./AppFooter.scss";
import { BUILD_VERSION } from "../../../../core/constants/build";

export interface AppFooterProps {
    readonly t?: TranslationFunction;
    readonly className?: string;
}

export function AppFooter (props: AppFooterProps) {
    const MENU_ITEMS = useMenuItems();
    const className = props?.className;
    const t: TranslationFunction = props?.t ?? ((key: string): string => key);
    return (
        <footer
            className={
                APP_FOOTER
                + (className ? ` ${className}` : '')
            }
        >
            <SEODiv className={`${APP_FOOTER}-content`}
                    path={FOOTER_ROUTE}
                    title={ t( T_FOOTER_TITLE ) }
                    description={ t( T_FOOTER_DESCRIPTION ) }
                    siteName={ t( T_SITE_NAME ) }
            >
                <div className={`${APP_FOOTER}-main-row`}>

                    <div className={`${APP_FOOTER}-logo`}>
                        <NavLink
                            className={`${APP_CLASS_NAME}-logo`}
                            to={INDEX_ROUTE}
                        ><SendanorLogoWhite /></NavLink>
                    </div>

                    <div className={`${APP_FOOTER}-item ${APP_FOOTER}-nav`}>
                        <div className="footer-title">{t(T_FOOTER_TITLE_SERVICES)}</div>
                        <NavMenu
                            t={t}
                            className={"footer-nav"}
                            items={MENU_ITEMS}
                        />
                    </div>

                    <div className={`${APP_FOOTER}-item ${APP_FOOTER}-customer-service`}>
                        <div className="footer-title">{t(T_FOOTER_TITLE_CUSTOMER_SERVICES)}</div>
                        <div>{t(T_SUPPORT_INFO)}</div>
                        <a href={t(T_SUPPORT_EMAIL_HREF)}>{t(T_SUPPORT_EMAIL)}</a>
                        <div className="footer-2-line">
                            <PhoneLink phone={COMPANY_PHONE_NUMBER_DISPLAY} />
                        </div>
                        <div className="footer-title">{t(T_FOOTER_ADDRESS_TITLE)}</div>
                        <div className="footer-2-line">
                            {COMPANY_ADDRESS_LINE_1}<br />
                            {COMPANY_ADDRESS_LINE_2}
                        </div>
                        <div className="footer-title">{t(T_FOOTER_MAINTENANCE_24)}</div>
                        <div className="footer-2-line">
                            <PhoneLink phone={MAINTENANCE_PHONE_NUMBER_DISPLAY} />
                        </div>
                    </div>

                    <div className={`${APP_FOOTER}-item ${APP_FOOTER}-legal`}>
                        <div className="footer-title">{t(T_FOOTER_TITLE_TERMS_LEGALS)}</div>
                        <div><NavLink to={TERMS_OF_DELIVERY_ROUTE}>{t(T_FOOTER_TERMS_OF_DELIVERY)}</NavLink></div>
                        {/*<div><NavLink to={TERMS_OF_USE_ROUTE}>{t(T_FOOTER_TERMS_OF_USE)}</NavLink></div>*/}
                        <div><NavLink to={PRIVACY_POLICY_ROUTE}>{t(T_FOOTER_PRIVACY_POLICY)}</NavLink></div>
                        <div><NavLink to={COOKIES_ROUTE}>{t(T_FOOTER_COOKIES)}</NavLink></div>
                    </div>

                    <div className={ `${ APP_FOOTER }-item ${ APP_FOOTER }-links` }>

                        <div className="footer-title">{ t( T_FOOTER_TITLE_LINKS ) }</div>

                        <div>
                            <a
                                href={ SENDANOR_NOTIFICATIONS_WEBSITE }
                                target="blank"
                            >{ t( T_NOTIFICATIONS ) }</a>
                        </div>
                        <div>
                            <a
                                href={ SENDANOR_MAIL_WEBSITE }
                                target="blank"
                            >mail.nor.fi</a>
                        </div>
                        <div>
                            <a
                                href={ SENDANOR_SHELL_WEBSITE }
                                target="blank"
                            >shell.nor.fi</a>
                        </div>
                        <div>
                            <a
                                href={ SENDANOR_VPS_WEBSITE }
                                target="blank"
                            >vps.nor.fi</a>
                        </div>
                        <div>
                            <a
                                href={ SENDANOR_PROJECT_WEBSITE }
                                target="blank"
                            >Verkkopalvelumme kehitys</a>
                        </div>

                        <SocialMediaLinkList
                            className={ `${ APP_FOOTER }-some` }
                            discordUrl={ COMPANY_DISCORD_URL }
                            facebookUrl={ COMPANY_FACEBOOK_URL }
                            githubUrl={ COMPANY_GITHUB_URL }
                            linkedinUrl={ COMPANY_LINKEDIN_URL }
                            twitterUrl={ COMPANY_TWITTER_URL }
                        />

                        <div>
                            <a href={SENDANOR_PROJECT_TRACKER_WEBSITE}>v{BUILD_VERSION}</a>
                        </div>

                    </div>

                </div>

                <div className={ `${ APP_FOOTER }-secondary-row` }>
                    <LegalInfo
                        className={ `${ APP_FOOTER }-item ${ APP_FOOTER }-copyright` }
                        t={ t }
                    />
                    <div className={ `${ APP_FOOTER }-item ${ APP_FOOTER }-payment-options`}>
                        {PAYTRAIL_ENABLED?(
                            <PaymentOptionList className={`${APP_FOOTER}-payment-options-list`} />
                        ): null}
                    </div>
                </div>

            </SEODiv>

        </footer>
    );
}
